import { Box, Flex, useColorModeValue, useToken } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { baseKeymap } from '@remirror/pm/commands';
import { Command } from '@remirror/pm/dist-types/state';
import {
  EditorComponent,
  ReactComponentExtension,
  Remirror,
  ThemeProvider,
  useKeymap,
  useRemirror,
} from '@remirror/react';
import { AllStyledComponent } from '@remirror/styles/emotion';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExtensionPriority,
  InvalidContentHandler,
  RemirrorJSON,
} from 'remirror';
import {
  BoldExtension,
  DropCursorExtension,
  EventsExtension,
  HistoryExtension,
  ItalicExtension,
  MarkdownExtension,
  MentionAtomExtension,
  // NodeFormattingExtension,
  OrderedListExtension,
  StrikeExtension,
  UnderlineExtension,
} from 'remirror/extensions';
import UserSuggester from 'components/Editor/UserSuggester';
import { CustomLinkExtension } from 'components/Editor/CustomLinkExtension';
import * as Sentry from '@sentry/react';

export interface CommentEditorProps {
  initialData?: { [key: string]: any };
  onChange?: (content: string) => void;
  sendMessage: () => void;
  participantIds?: string[];
  spaceId?: string;
  chatKey?: number;
  placeholder?: string;
  onFocus?: () => void;
}

export const CommentEditor = (props: CommentEditorProps) => {
  return (
    <Box overflow="hidden">
      <Flex
        direction="column"
        align="center"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <TextEditor {...props} />
      </Flex>
    </Box>
  );
};

function getExtensions() {
  return () => [
    new BoldExtension(),
    new ItalicExtension(),
    new UnderlineExtension(),
    new StrikeExtension(),
    new CustomLinkExtension({
      autoLink: true,
      markOverride: { excludes: undefined },
    }),
    new OrderedListExtension(),
    new MarkdownExtension(),
    new HistoryExtension(),
    new DropCursorExtension(),
    new ReactComponentExtension(),
    new EventsExtension(),
    new MentionAtomExtension({
      matchers: [{ name: 'at', char: '@', matchOffset: 0 }],
    }),
  ];
}

function TextEditor({
  onChange,
  initialData,
  sendMessage,
  participantIds,
  spaceId,
  chatKey = 0,
  placeholder = t`Add a comment…`,
  onFocus,
}: CommentEditorProps) {
  const textColor = useColorModeValue('black', 'white');
  const backgroundColorToken = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.500');
  const backgroundColor = useToken('colors', backgroundColorToken);
  const onError: InvalidContentHandler = useCallback(
    ({ json, invalidContent, transformers, error }) => {
      Sentry.withScope((s) => {
        s.setLevel('warning');
        s.setTag('editor', 'AnnouncementsTextEditor');
        Sentry.captureException(error);
      });
      // Automatically remove all invalid nodes and marks.
      return transformers.remove(json, invalidContent);
    },
    [],
  );
  const { manager, state, getContext } = useRemirror({
    extensions: getExtensions(),
    content:
      initialData && (initialData as RemirrorJSON).type
        ? (initialData as RemirrorJSON)
        : undefined,
    onError,
  });
  const navigate = useNavigate();
  const [isUserSuggesterOpen, setIsUserSuggesterOpen] = React.useState(false);

  useEffect(() => {
    if (chatKey > 0) {
      getContext()?.clearContent();
    }
  }, [chatKey]);

  const hooks = [
    () => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useKeymap(
        'Shift-Enter',
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useCallback(({ state, dispatch, view }) => {
          const handler = baseKeymap['Enter'] as Command;

          return handler(state, dispatch, view);
        }, []),
        ExtensionPriority.Highest,
      );
    },
    () => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useKeymap(
        'Enter',
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useCallback(
          ({ next }) => {
            if (!isUserSuggesterOpen) {
              sendMessage();
            }

            next();

            return true;
          },
          [isUserSuggesterOpen],
        ),
        ExtensionPriority.Highest,
      );
    },
  ];

  return (
    <Box
      maxW="75ch"
      w="100%"
      h="100%"
      rounded="md"
      border="1px solid"
      borderColor={borderColor}
      bg={`var(--editor-bg-color, ${backgroundColor})`}
      color={`var(--editor-color, ${textColor})`}
      data-qa="chat-editor"
      sx={{
        '& .remirror-editor-wrapper': {
          paddingTop: '0 !important',
        },
        '& .suggest-at': {
          color: `${textColor} !important`,
        },
        '& .remirror-mention-atom': {
          padding: '0 !important',
          background: 'none !important',
          color: `${textColor} !important`,
          fontWeight: 'bold',
          textDecoration: 'none',
          cursor: 'pointer',
        },
        '& .remirror-is-empty:first-of-type::before': {
          fontStyle: 'unset !important',
        },
      }}
      userSelect="auto"
    >
      <AllStyledComponent style={{ height: '100%' }}>
        <ThemeProvider
          theme={{
            space: { 3: '10px', 6: '60px' },
            color: { text: textColor },
            radius: { border: 1 },
          }}
        >
          <Remirror
            manager={manager}
            initialContent={state}
            // autoFocus={true}
            placeholder={placeholder}
            onChange={(e) => {
              onChange?.(JSON.stringify(e.state.doc.toJSON()));
            }}
            editable={true}
            hooks={hooks}
            onFocus={onFocus}
          >
            <UserSuggester
              participantIds={participantIds}
              spaceId={spaceId}
              setIsUserSuggesterOpen={setIsUserSuggesterOpen}
            />
            <Box
              overflow="hidden"
              height="100%"
              onClick={(e) => {
                if (e.target instanceof HTMLAnchorElement) {
                  const link = e.target as HTMLAnchorElement;
                  window.__handleLinkClick && window.__handleLinkClick(e);
                }
              }}
            >
              <EditorComponent />
            </Box>
            {/* <CommentFloatingToolbar editable /> */}
          </Remirror>
        </ThemeProvider>
      </AllStyledComponent>
    </Box>
  );
}

export default CommentEditor;
